import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Input,
  Button,
  Typography,
  Stack,
  Alert,
  Box,
  Link,
  Stepper,
  Step,
  StepButton,
  StepIndicator,
  CircularProgress,
} from '@mui/joy';
import { useNotification } from '../../context/NotificationContext';
import { useLoading } from '../../context/LoadingContext';
import AuthLayout from '../../components/Layout/AuthLayout';

const ForgotPasswordPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [demoOtp, setDemoOtp] = useState(''); // For development
  const [resendTimer, setResendTimer] = useState(0);

  const { showSuccess, showError } = useNotification();
  const { setLoading, isLoading } = useLoading();
  const navigate = useNavigate();

  // API configuration
  const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://tv.vectorindia.org/api' : 'http://localhost:6011/api';

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const sendOtp = async () => {
    setLoading('sendOtp', true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpSent(true);
        setCurrentStep(1);
        setResendTimer(60); // 60 seconds before allowing resend

        // Store demo OTP if available (development mode)
        if (result.demoOtp) {
          setDemoOtp(result.demoOtp);
        }

        showSuccess(`OTP sent to ${email}`);
      } else {
        setError(result.error || 'Failed to send OTP');
        if (result.error === 'Email not found in system') {
          showError('This email is not registered in our system.');
        } else {
          showError('Failed to send OTP. Please try again.');
        }
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setError('Network error. Please check your connection.');
      showError('Failed to send OTP. Please try again.');
    } finally {
      setLoading('sendOtp', false);
    }
  };

  const verifyOtp = async () => {
    setLoading('verifyOtp', true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          otp: otp
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpVerified(true);
        setCurrentStep(2);
        showSuccess('OTP verified successfully');
      } else {
        setError(result.error || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading('verifyOtp', false);
    }
  };

  const resetPassword = async () => {
    setLoading('resetPassword', true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          newPassword: newPassword,
          otpVerified: otpVerified
        }),
      });

      const result = await response.json();

      if (result.success) {
        setCurrentStep(3);
        setSuccess('Password reset successfully!');
        showSuccess('Password reset successfully! Redirecting to login...');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.error || 'Failed to reset password');
        showError('Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please check your connection.');
      showError('Failed to reset password. Please try again.');
    } finally {
      setLoading('resetPassword', false);
    }
  };

  const handleStep0Submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    await sendOtp();
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError('Please enter the OTP');
      return;
    }
    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }
    await verifyOtp();
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    await resetPassword();
  };

  const resendOtp = async () => {
    setOtp('');
    setError('');
    await sendOtp();
  };

  const steps = [
    { label: 'Enter Email', description: 'Verify your identity' },
    { label: 'Email Verification', description: 'Enter OTP from email' },
    { label: 'New Password', description: 'Set your new password' }
  ];

  return (
    <AuthLayout>
      <Card
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '600px',
          p: 4,
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          borderRadius: '16px',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography level="h2" sx={{ mb: 1, fontWeight: 600 }}>
              Forgot Password?
            </Typography>
            <Typography level="body-md" sx={{ color: 'neutral.600' }}>
              No worries! We'll help you reset your password.
            </Typography>
          </Box>

          {/* Progress Stepper */}
          {currentStep < 3 && (
            <Box sx={{ mb: 4 }}>
              <Stepper sx={{ width: '100%' }}>
                {steps.map((step, index) => (
                  <Step
                    key={step.label}
                    indicator={
                      <StepIndicator
                        variant={index <= currentStep ? 'solid' : 'outlined'}
                        color={index < currentStep ? 'success' : index === currentStep ? 'primary' : 'neutral'}
                      >
                        {index < currentStep ? '✓' : index + 1}
                      </StepIndicator>
                    }
                    sx={{
                      '&::after': {
                        ...(index <= currentStep && {
                          bgcolor: 'primary.solidBg',
                        }),
                      },
                    }}
                  >
                    <StepButton>
                      <Typography level="title-sm">{step.label}</Typography>
                      <Typography level="body-xs">{step.description}</Typography>
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {error && (
            <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="success" variant="soft" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Step 0: Enter Email */}
          {currentStep === 0 && (
            <form onSubmit={handleStep0Submit}>
              <Stack spacing={3}>
                <Typography level="title-md">Enter your email address</Typography>

                <FormControl>
                  <Input
                    type="email"
                    placeholder="your.email@vectorindia.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    size="lg"
                    autoComplete="email"
                  />
                </FormControl>

                <Button
                  type="submit"
                  size="lg"
                  loading={isLoading('sendOtp')}
                  sx={{
                    background: 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d16000 0%, #e67700 100%)',
                    },
                  }}
                >
                  Send OTP to Email
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component={RouterLink}
                    to="/login"
                    level="body-sm"
                  >
                    Back to Login
                  </Link>
                </Box>
              </Stack>
            </form>
          )}

          {/* Step 1: Email OTP Verification */}
          {currentStep === 1 && (
            <form onSubmit={handleStep1Submit}>
              <Stack spacing={3}>
                <Typography level="title-md">Email Verification</Typography>

                <Alert color="info" variant="soft">
                  We've sent a 6-digit verification code to <strong>{email}</strong>
                </Alert>

                <FormControl>
                  <FormLabel>Enter OTP</FormLabel>
                  <Box sx={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      autoFocus
                      sx={{
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        letterSpacing: '0.5rem',
                        width: '100%',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        padding: '10px',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        '&:focus': {
                          borderColor: '#eb7a24',
                          boxShadow: '0 0 5px rgba(235, 122, 36, 0.5)',
                        },
                      }}
                    />
                  </Box>
                </FormControl>

                {/* Demo OTP Display (Development Mode) */}
                {demoOtp && (
                  <Alert color="warning" variant="soft">
                    <Typography level="body-sm">
                      <strong>Demo OTP:</strong> {demoOtp}
                    </Typography>
                    <Typography level="body-xs" sx={{ mt: 1 }}>
                      This is shown for development/testing purposes only
                    </Typography>
                  </Alert>
                )}

                <Button
                  type="submit"
                  size="lg"
                  loading={isLoading('verifyOtp')}
                  sx={{
                    background: 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d16000 0%, #e67700 100%)',
                    },
                  }}
                >
                  Verify OTP
                </Button>

                <Button
                  variant="plain"
                  size="sm"
                  onClick={resendOtp}
                  disabled={resendTimer > 0}
                  loading={isLoading('sendOtp')}
                >
                  {resendTimer > 0
                    ? `Resend OTP in ${resendTimer}s`
                    : 'Resend OTP'
                  }
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component={RouterLink}
                    to="/login"
                    level="body-sm"
                  >
                    Cancel and return to Login
                  </Link>
                </Box>
              </Stack>
            </form>
          )}

          {/* Step 2: Set New Password */}
          {currentStep === 2 && (
            <form onSubmit={handleStep2Submit}>
              <Stack spacing={3}>
                <Typography level="title-md">Set New Password</Typography>

                <Alert color="success" variant="soft">
                  Email verified! Now set your new password.
                </Alert>

                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <Box sx={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
                    <Input
                      type="password"
                      placeholder="Enter new password (min 8 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      autoFocus
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        padding: '10px',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        '&:focus': {
                          borderColor: '#eb7a24',
                          boxShadow: '0 0 5px rgba(235, 122, 36, 0.5)',
                        },
                      }}
                    />
                  </Box>
                </FormControl>

                <FormControl>
                  <FormLabel>Confirm New Password</FormLabel>
                  <Box sx={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
                    <Input
                      type="password"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        padding: '10px',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        '&:focus': {
                          borderColor: '#eb7a24',
                          boxShadow: '0 0 5px rgba(235, 122, 36, 0.5)',
                        },
                      }}
                    />
                  </Box>
                </FormControl>

                <Button
                  type="submit"
                  size="lg"
                  loading={isLoading('resetPassword')}
                  color="success"
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    },
                  }}
                >
                  Reset Password
                </Button>
              </Stack>
            </form>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <Stack spacing={3} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  borderRadius: '50%',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '40px',
                }}
              >
                ✓
              </Box>

              <Typography level="h3" sx={{ fontWeight: 600, color: 'success.500' }}>
                Password Reset Successfully!
              </Typography>

              <Typography level="body-lg" sx={{ color: 'neutral.600' }}>
                Your password has been reset. Redirecting to login...
              </Typography>

              <CircularProgress color="success" />
            </Stack>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;